<template>
  <!-- Not click-to-close: a half-filled recipient list is easy to lose to a stray click. -->
  <modal
    v-model="isOpen"
    width="560"
    :clickToClose="false"
    content-class="share-dialog-content"
  >
    <div class="position-relative modal-outer-container">
      <div class="modal-header border-bottom">
        <h5 class="mb-0 ml-3 pl-2 share-title">
          <template v-if="!documentIds.length && folder"
            >Share "<truncated-text :text="folder.name" max="30ch" />"</template
          >
          <template v-else>{{ title }}</template>
        </h5>
        <button class="btn border-0" aria-label="Close" @click="close">
          <i class="fas fa-times fa-lg"></i>
        </button>
      </div>

      <div class="modal-body">
        <label class="font-weight-bold" for="shareRecipients">
          {{ recipientsLabel }}
        </label>
        <multiselect
          id="shareRecipients"
          v-model="recipients"
          class="app-select no-labels mb-3"
          :options="people"
          :multiple="true"
          :searchable="true"
          :preserve-search="true"
          :close-on-select="false"
          :clear-on-select="false"
          :show-labels="false"
          :limit="3"
          :max-height="220"
          :loading="isLoadingPeople"
          label="name"
          track-by="id"
          placeholder="Search for a person"
          @update:modelValue="refreshPreview"
        >
          <template #option="props">
            <div class="d-flex align-items-center justify-content-between">
              <span class="font-weight-bold select-label">{{
                props.option.name
              }}</span>
              <span class="small font-weight-bold select-sublabel ml-3">
                ({{ roleFor(props.option) }})
              </span>
            </div>
          </template>
          <template #noResult>
            <div class="no-result">No one matching your search.</div>
          </template>
        </multiselect>

        <div class="custom-control custom-checkbox mb-1">
          <input
            type="checkbox"
            class="custom-control-input"
            id="documentsDirectShare"
            v-model="directShare"
          />
          <label class="custom-control-label" for="documentsDirectShare">
            {{ directShareLabel }}
          </label>
        </div>
        <div class="small text-muted-theme mb-3 ml-4 pl-1">
          They will be able to open {{ subjectNoun }} themselves, and will be
          notified by email and text.
        </div>

        <!-- The last result stays on screen while a new one loads, so the dialog keeps its
           height instead of collapsing to a spinner and growing back. -->
        <div
          v-if="previewText"
          class="alert share-preview-alert py-2 px-4 mb-3"
          :class="{ 'is-refreshing': isPreviewing }"
        >
          <span
            v-if="isPreviewing"
            class="spinner-border spinner-border-sm mr-2"
          ></span>
          <span v-html="previewText"></span>
          <template v-if="listedPeople.length > NAME_LIMIT">
            <button
              type="button"
              class="btn btn-link p-0 ml-1 align-baseline share-show-all"
              :aria-expanded="showAllPeople"
              @click="showAllPeople = !showAllPeople"
            >
              {{
                showAllPeople ? "Hide list" : `Show all ${listedPeople.length}`
              }}
            </button>
            <ul v-if="showAllPeople" class="share-people-list">
              <li v-for="person in listedPeople" :key="person.id">
                {{ person.name }}
              </li>
            </ul>
          </template>
        </div>
        <div v-else-if="isPreviewing" class="alert alert-light py-2 px-3 mb-3">
          <span class="spinner-border spinner-border-sm mr-2"></span>Working out
          who gains access…
        </div>
        <div v-else-if="previewFailed" class="alert alert-light py-2 px-3 mb-3">
          Could not check who would gain access — you can still share.
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-transparent mr-2" @click="close">Cancel</button>
        <save
          :saving="isSharing"
          :should-disable="!recipients.length"
          @click="submit"
          classes="btn btn-theme"
        >
          {{ confirmLabel }}
        </save>
      </div>
    </div>
  </modal>
</template>

<script>
import Multiselect from "vue-multiselect";
import Swal from "sweetalert2";
import { debounce } from "lodash";
import { mapActions, mapState } from "vuex";
import { PREVIEW_DEBOUNCE_MS, roleLabel } from "./useDocumentsArea";

// Longer lists name this many people and count the rest.
const NAME_LIMIT = 5;

export default {
  name: "ShareDialog",
  components: { Multiselect },
  props: {
    modelValue: { type: Boolean, default: false },
    folder: { type: Object, default: null },
    documentIds: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "shared"],
  data() {
    return {
      recipients: [],
      directShare: false,
      people: [],
      isLoadingPeople: false,
      preview: null,
      isPreviewing: false,
      previewFailed: false,
      previewToken: 0,
      showAllPeople: false,
      NAME_LIMIT,
    };
  },
  computed: {
    ...mapState({ isSharing: (state) => state.folders.isSharing }),

    isOpen: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },

    /** The folder's own name is rendered in the template, so it can be shortened. */
    title() {
      if (!this.documentIds.length) {
        return "Share";
      }

      return this.documentIds.length === 1
        ? "Share 1 document"
        : `Share ${this.documentIds.length} documents`;
    },

    subjectNoun() {
      return this.documentIds.length ? "these" : "this";
    },

    /** Matches the wording the Documents area used before this screen was rebuilt. */
    recipientsLabel() {
      return this.documentIds.length
        ? "Share selected document with"
        : "Share selected folder with";
    },

    directShareLabel() {
      return `Share directly with tagged ${this.$customerLabel(
        "plural",
        false
      )}`;
    },

    /** Who "Show all" lists: those who will open it, otherwise those it is only filed for. */
    listedPeople() {
      const gaining = (this.preview && this.preview.gaining) || [];
      const opensIt = gaining.filter((p) => p.real_access);

      return opensIt.length ? opensIt : gaining.filter((p) => !p.real_access);
    },

    confirmLabel() {
      const opensIt = ((this.preview && this.preview.gaining) || []).filter(
        (p) => p.real_access
      );

      if (!opensIt.length) {
        return "Share";
      }

      return opensIt.length === 1
        ? "Share with 1 person"
        : `Share with ${opensIt.length} people`;
    },

    /**
     * `gaining` is everyone who would newly gain access; `cascaded` is the subset added
     * automatically. Rendered with `v-html` — every name is escaped in `quoted()`.
     */
    previewText() {
      if (!this.preview) {
        return "";
      }

      const gaining = this.preview.gaining || [];
      const cascaded = this.preview.cascaded || [];
      const sentences = [];

      const alreadyHasAccess = (this.preview.recipients || []).filter(
        (r) => !gaining.some((g) => g.id === r.id)
      );

      if (alreadyHasAccess.length) {
        const names = this.nameList(alreadyHasAccess);
        const verb =
          alreadyHasAccess.length === 1 ? "already has" : "already have";

        sentences.push(`${names} ${verb} access.`);
      }

      const opensIt = gaining.filter((p) => p.real_access);
      const filedOnly = gaining.filter((p) => !p.real_access);

      if (opensIt.length) {
        const count =
          opensIt.length === 1
            ? "1 person will gain access:"
            : `${opensIt.length} people will gain access:`;

        sentences.push(`${count} ${this.nameList(opensIt)}.`);

        if (cascaded.length) {
          const team = cascaded[0].reason_detail;
          const teamClause = team
            ? `${this.quoted(team)}'s care team`
            : "their care team";

          // A long care team would repeat most of the names above, so it is counted instead.
          const who =
            cascaded.length > NAME_LIMIT
              ? `${cascaded.length} of them`
              : this.andJoin(cascaded.map((p) => this.quoted(p.name)));

          sentences.push(
            `${who} ${
              cascaded.length === 1 ? "is" : "are"
            } added automatically as part of ${teamClause}.`
          );
        }
      }

      // Unticked files the folder against their record rather than giving it to them.
      if (filedOnly.length) {
        const names = this.nameList(filedOnly);

        sentences.push(
          `${names} will not be able to open this — tick &quot;${this.escaped(
            this.directShareLabel
          )}&quot; above to give it to them.`
        );

        // Otherwise the share looks like it did nothing.
        if (!opensIt.length) {
          sentences.push("No one else gains access they did not already have.");
        }
      }

      // A share reaches everything nested inside.
      const subfolders = this.preview.subfolder_count || 0;
      if (subfolders) {
        sentences.push(
          `Everything inside this folder, including its ${subfolders} ${
            subfolders === 1 ? "subfolder" : "subfolders"
          }, is shared too.`
        );
      }

      return sentences.join(" ");
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.reset();
        this.loadPeople();
      }
    },
    directShare() {
      this.refreshPreview();
    },
  },
  created() {
    this.refreshPreview = debounce(() => {
      const token = ++this.previewToken;

      if (!this.folder || !this.recipients.length) {
        this.preview = null;
        this.previewFailed = false;
        this.isPreviewing = false;

        return;
      }

      // Snapshotted, so the rendered sentences always describe one consistent set of people:
      // `recipients` updates the instant you pick someone, the response arrives later.
      const asked = [...this.recipients];

      this.isPreviewing = true;
      this.previewFailed = false;
      this.previewShare({
        folderId: this.folder.id,
        userIds: asked.map((r) => r.id),
        directShare: this.directShare,
      })
        .then((data) => {
          if (token === this.previewToken) {
            this.preview = { ...data, recipients: asked };
          }
        })
        .catch(() => {
          if (token === this.previewToken) {
            // A failed preview must not block the share, but must not fail silently either.
            this.preview = null;
            this.previewFailed = true;
          }
        })
        .finally(() => {
          if (token === this.previewToken) {
            this.isPreviewing = false;
          }
        });
    }, PREVIEW_DEBOUNCE_MS);
  },
  beforeUnmount() {
    this.refreshPreview.cancel();
  },
  methods: {
    ...mapActions({
      previewShare: "folders/previewShare",
      shareFiles: "folders/shareFiles",
      shareFolders: "folders/shareFolders",
    }),

    roleFor(person) {
      return roleLabel(this, person.role_id);
    },

    /** Escapes a name and wraps it as `<strong>"Name"</strong>` for the v-html bubble. */
    escaped(text) {
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },

    quoted(name) {
      return `<strong>&quot;${this.escaped(name)}&quot;</strong>`;
    },

    /** Everyone when the list is short; otherwise the first few and "N others". */
    nameList(people) {
      const names = people.map((p) => this.quoted(p.name));

      if (names.length <= NAME_LIMIT) {
        return this.andJoin(names);
      }

      const others = names.length - NAME_LIMIT;

      return `${names.slice(0, NAME_LIMIT).join(", ")} and ${others} ${
        others === 1 ? "other" : "others"
      }`;
    },

    /** "A", "A and B", or "A, B and C". */
    andJoin(items) {
      if (items.length <= 2) {
        return items.join(" and ");
      }

      return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
    },

    reset() {
      this.recipients = [];
      this.directShare = false;
      this.preview = null;
      this.isPreviewing = false;
      this.previewFailed = false;
      this.showAllPeople = false;
    },

    close() {
      this.isOpen = false;
    },

    async loadPeople() {
      if (this.people.length) {
        return;
      }
      this.isLoadingPeople = true;

      try {
        const res = await this.$http.get("all-users-info");
        this.people = (res.data.data || [])
          .filter((user) => this.$isActiveUserStatus(user.status))
          .map((u) => ({ id: u.id, name: u.name, role_id: u.role_id }));
      } catch (err) {
        Swal.fire("Error", "Could not load the list of people.", "error");
      } finally {
        this.isLoadingPeople = false;
      }
    },

    submit() {
      const userIds = this.recipients.map((r) => r.id);

      const request = this.documentIds.length
        ? this.shareFiles({
            users_id: `[${userIds}]`,
            file_ids: `[${this.documentIds}]`,
            directShare: this.directShare,
          })
        : this.shareFolders({
            users_id: `[${userIds}]`,
            folders_id: `[${this.folder.id}]`,
            directShare: this.directShare,
          });

      request.then((ok) => {
        if (ok) {
          this.$emit("shared");
          this.close();
        }
      });
    },
  },
};
</script>

<style scoped>
/* The shared `.alert-info` theme class pairs a mid-blue background with mid-blue text (see
   theme.css) — legible for some, not for everyone. Overridden locally rather than in
   theme.css, which many unrelated screens also use. */
.share-preview-alert {
  max-height: 40vh;
  overflow-y: auto;
  background-color: #eaf4fb;
  border-color: #bfe0f2;
  color: #0b3d54;
}

.share-show-all {
  color: #0b6aa2;
  font-weight: 700;
  text-decoration: underline;
}

.share-people-list {
  max-height: 190px;
  overflow-y: auto;
  margin: 8px 0 4px;
  padding: 6px 12px;
  list-style: none;
  columns: 2;
  column-gap: 18px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.65);
  font-size: 13.5px;
}

.share-people-list li {
  break-inside: avoid;
  padding: 3px 0;
  border-bottom: 1px solid rgba(11, 61, 84, 0.08);
}

@media (max-width: 480px) {
  .share-people-list {
    columns: 1;
  }
}

.share-preview-alert.is-refreshing {
  opacity: 0.55;
}

.share-title {
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 1.15rem;
  font-weight: 700;
}
</style>

<!-- Unscoped: BaseModal teleports its content to <body>, so a scoped rule cannot reach it.
     `.ccc-modal-content` is `overflow: hidden`, which clips the recipient list; this dialog
     needs it to open over the page like any other dropdown. -->
<style>
.share-dialog-content,
.share-dialog-content .modal-body {
  overflow: visible !important;
}
</style>
